import * as React from 'react'
import { ShouldRevalidateFunction } from 'react-router'

import { shouldRevalidateOnQueryChange } from '../../util-routing'
import { LazyRouteDefinition, createRouteViewSwitch } from '../../util-routing'
import { QuoteQuery, QuoteSubview } from './types'
import { getQuoteSubview } from './utils'

const LazyQuoteSharedWrapper = () =>
  import(/* webpackChunkName: "quote-portal-components" */ './components/QuoteSharedWrapper')
function withQuoteRoot(LazyComponent: () => Promise<{ default: React.ComponentType }>) {
  return async () => {
    const [componentModule, quoteSharedWrapperModule] = await Promise.all([LazyComponent(), LazyQuoteSharedWrapper()])

    const Component = componentModule.default
    const QuoteSharedWrapper = quoteSharedWrapperModule.default

    return {
      default: function QuoteRoot() {
        return (
          <>
            <QuoteSharedWrapper />

            <Component />
          </>
        )
      },
    }
  }
}

export function getShouldRevalidateQuoteRoute(): ShouldRevalidateFunction {
  return (args) => {
    const route = getQuoteSubview(args.currentUrl.searchParams.get('ty') as QuoteSubview)

    switch (route) {
      case QuoteSubview.OptionsChain:
      case QuoteSubview.OptionsVolatility:
        return shouldRevalidateOnQueryChange(args, ['e', 's', 'ov'])

      default:
        return false
    }
  }
}

const optionsBundle = {
  loader: () => import(/* webpackChunkName: "options-loaders" */ '../options/loaders'),
  LazyComponent: withQuoteRoot(() => import(/* webpackChunkName: "options" */ '../options')),
}

const { loader, action, lazySwitch } = createRouteViewSwitch({
  defaultView: '',
  viewParam: QuoteQuery.Type,
  views: {
    '': {
      LazyComponent: LazyQuoteSharedWrapper,
    },
    [QuoteSubview.ComparePerf]: {
      LazyComponent: withQuoteRoot(() => import(/* webpackChunkName: "compare-perf" */ '../compare-perf')),
    },
    [QuoteSubview.OptionsChain]: optionsBundle,
    [QuoteSubview.OptionsVolatility]: optionsBundle,
    [QuoteSubview.Revenue]: {
      LazyComponent: withQuoteRoot(() => import(/* webpackChunkName: "revenue" */ '../revenue')),
    },
    [QuoteSubview.Dividends]: {
      LazyComponent: withQuoteRoot(() => import(/* webpackChunkName: "dividends" */ '../dividends')),
    },
    [QuoteSubview.Earnings]: {
      LazyComponent: withQuoteRoot(() => import(/* webpackChunkName: "earnings" */ '../earnings')),
    },
    [QuoteSubview.Forecast]: {
      LazyComponent: withQuoteRoot(() => import(/* webpackChunkName: "forecast" */ '../forecast')),
    },
    [QuoteSubview.ShortInterest]: {
      LazyComponent: withQuoteRoot(() => import(/* webpackChunkName: "short-interest" */ '../short-interest')),
    },
    [QuoteSubview.Traffic]: {
      LazyComponent: withQuoteRoot(() => import(/* webpackChunkName: "traffic" */ '../traffic')),
    },
    [QuoteSubview.LatestFilings]: {
      LazyComponent: withQuoteRoot(() => import(/* webpackChunkName: "latest-filings" */ '../latest-filings')),
    },
  },
})

export const quoteModule: LazyRouteDefinition[] = [
  {
    path: '/quote.ashx',
    loader,
    action,
    shouldRevalidate: getShouldRevalidateQuoteRoute(),
    lazy: lazySwitch,
  },
]
